import type { CheckInStreak } from '@techsofit/api-types';
import { cn } from '@techsofit/ui';

function weekCopy(n: number): string {
  if (n <= 0) return 'Start a week streak';
  if (n === 1) return '1 week on the board';
  return `${n} weeks in a row`;
}

function dayCopy(n: number): string {
  if (n <= 0) return 'Show up tomorrow to start a daily streak';
  if (n === 1) return '1 day this week';
  return `${n} days in a row this week`;
}

/** Strava-style Mon–Sun strip: trained vs skipped vs upcoming. */
export function StreakWeekCard({
  streak,
  name,
  className,
}: {
  streak: CheckInStreak;
  name?: string;
  className?: string;
}) {
  const who = name ? `${name.split(/\s+/)[0]}'s week` : 'This week';
  return (
    <div className={cn('rounded-card bg-card p-4 text-left shadow-card', className)}>
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-medium text-ink">{who}</p>
        <p className="font-display text-sm font-semibold text-ink">{weekCopy(streak.weekStreak)}</p>
      </div>
      <div className="mt-3 grid grid-cols-7 gap-1.5">
        {streak.week.map((d) => (
          <div key={d.date} className="flex flex-col items-center gap-1">
            <span className="text-[10px] font-medium uppercase tracking-wide text-muted">{d.label}</span>
            <span
              title={d.visited ? 'Checked in' : d.future ? 'Upcoming' : 'Skipped'}
              className={cn(
                'grid size-8 place-items-center rounded-full text-[11px] font-semibold tabular-nums',
                d.visited && 'bg-accent text-accent-ink',
                !d.visited && !d.future && 'bg-surface text-muted ring-1 ring-inset ring-line',
                d.future && 'bg-transparent text-muted/50 ring-1 ring-dashed ring-line',
              )}
            >
              {d.visited ? '✓' : d.future ? '·' : '–'}
            </span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-muted">
        {streak.daysThisWeek} day{streak.daysThisWeek === 1 ? '' : 's'} this week
        {streak.dayStreak > 0 ? ` · ${dayCopy(streak.dayStreak)}` : ''}
      </p>
    </div>
  );
}
